import { useTranslation } from '@cobliteam/i18n';
import { usePermissions } from '@cobliteam/permissions';
import Button from '@cobliteam/ui/components/Button';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Icon from '@cobliteam/ui/icons';
import type { DriverDrawerStep, DriverDrawerType } from '../types';

export interface FooterButtonsProps {
  canSubmit?: boolean;
  type: DriverDrawerType;
  step: DriverDrawerStep;
  manualMode: boolean;
  isLoading?: boolean;
  onFormDoneClick: () => void;
  onImagesDoneClick: () => void;
  onImagesBack: () => void;
  onImagesConfirm: () => void;
  onCancelClickButton: () => void;
  onDeleteClickButton: () => void;
}

export const FooterButtons = ({
  canSubmit,
  step = 'form',
  type = 'add',
  manualMode,
  isLoading = false,
  onFormDoneClick,
  onImagesDoneClick,
  onImagesBack,
  onImagesConfirm,
  onCancelClickButton,
  onDeleteClickButton,
}: FooterButtonsProps) => {
  const { t } = useTranslation('ui-business');
  const { isAccessAllowed } = usePermissions();
  const canEditDriver = isAccessAllowed('create-update-delete:driver');

  const notAllowed = !canEditDriver && step === 'form';
  const submitDisabled = !canSubmit || notAllowed;
  const showDelete = type === 'edit' && step === 'form';
  const showImagesBack = step === 'images' && (!manualMode || type === 'add');

  const imagesPrimaryLabel = type === 'add' ? t('drawers.driver.save') : t('drawers.driver.confirm');

  return (
    <div className='flex w-full items-center gap-2'>
      {showDelete && (
        <Tooltip enable={!canEditDriver} content={t('drawers.driver.not_allowed_to_delete')}>
          <Button variant='ghost' disabled={!canEditDriver} color='danger' startIcon={<Icon name='trash' />} onClick={canEditDriver ? onDeleteClickButton : undefined}>
            {t('drawers.driver.delete_driver')}
          </Button>
        </Tooltip>
      )}
      {showImagesBack && (
        <Button variant='ghost' onClick={onImagesBack}>
          {t('drawers.driver.back')}
        </Button>
      )}
      <div className='flex-1' />
      <Button variant='ghost' onClick={onCancelClickButton}>
        {t('drawers.driver.cancel')}
      </Button>
      {step === 'form' && (
        <Tooltip enable={notAllowed} content={t('drawers.driver.not_allowed_to_add_or_edit')}>
          <Button onClick={onFormDoneClick} disabled={submitDisabled} loading={isLoading}>
            {t('drawers.driver.save')}
          </Button>
        </Tooltip>
      )}
      {step === 'images' && manualMode && (
        <Button onClick={onImagesDoneClick} disabled={!canSubmit} loading={isLoading}>
          {imagesPrimaryLabel}
        </Button>
      )}
      {step === 'images' && !manualMode && (
        <Button onClick={onImagesConfirm} disabled={isLoading} loading={isLoading}>
          {t('drawers.driver.confirm')}
        </Button>
      )}
    </div>
  );
};

export default FooterButtons;
